import { useCallback, useEffect, useState } from 'react';

import { rage, useObservable } from '~/rage';
import { HandlingValue, IVector3, VehicleHandling } from '~/shared';

import _ from 'lodash';
import {
	ButtonContainer,
	ContentContainer,
	ContentContainerWrapper,
} from '../components/ContentContainer';
import { ContentInputFloat } from '../components/ContentInput';
import { ContentInputFlag } from '../components/ContentInput/ContentInputFlag.styled';
import { ContentInputFlagContainer } from '../components/ContentInputFlagContainer';
import { ContentInputRange } from '../components/ContentInputRange';
import { ContentRowContainer } from '../components/ContentRowContainer';
import { ContentRowText } from '../components/ContentRowText';
import { PanelContainer } from '../components/PanelContainer';
import { TabContainer } from '../components/TabContainer';
import { TabListContainer } from '../components/TabListContainer';

interface IHandlingTab {
	id: string;
	name: string;
	index: number;
}

interface IHandlingContent {
	id: string;
	name?: string;
	type?: 'float' | 'vector3' | 'flag';
	range?: [number, number];
	value: HandlingValue | null;
	index: number;
}

interface IHandlingDeclaration {
	id: string;
	name: string;
	props: Omit<IHandlingContent, 'value' | 'index'>[];
}

const handlingTpl = `<?xml version="1.0" encoding="UTF-8"?>

<CHandlingDataMgr>
  <HandlingData>
    <Item type="CHandlingData">
      <handlingName>EDIT_ME_PLS</handlingName>
      <fMass value="" />
      <fInitialDragCoeff value="" />
      <fPercentSubmerged value="" />
      <vecCentreOfMassOffset x="" y="" z="" />
      <vecInertiaMultiplier x="" y="" z="" />
      <fDriveBiasFront value="" />
      <nInitialDriveGears value="" />
      <fInitialDriveForce value="" />
      <fInitialDriveMaxFlatVel value="100" />
      <fDriveInertia value="" />
      <fClutchChangeRateScaleUpShift value="" />
      <fClutchChangeRateScaleDownShift value="" />
      <fDriveMaxFlatVel value="" />
      <fBrakeForce value="" />
      <fBrakeBiasFront value="" />
      <fHandBrakeForce value="" />
      <fSteeringLock value="" />
      <fTractionCurveMax value="" />
      <fTractionCurveMin value="" />
      <fTractionCurveLateral value="" />
      <fTractionSpringDeltaMax value="" />
      <fLowSpeedTractionLossMult value="" />
      <fTractionBiasFront value="" />
      <fTractionLossMult value="" />
      <fSuspensionForce value="" />
      <fSuspensionCompDamp value="" />
      <fSuspensionReboundDamp value="" />
      <fSuspensionUpperLimit value="" />
      <fSuspensionLowerLimit value="" />
      <fSuspensionRaise value="" />
      <fSuspensionBiasFront value="" />
      <fAntiRollBarForce value="" />
      <fAntiRollBarBiasFront value="" />
      <fRollCentreHeightFront value="" />
      <fRollCentreHeightRear value="" />
      <fCollisionDamageMult value="" />
      <fWeaponDamageMult value="" />
      <fDeformationDamageMult value="" />
      <fEngineDamageMult value="" />
      <fPetrolTankVolume value="" />
      <fOilVolume value="" />
      <nMonetaryValue value="" />
      <strModelFlags></strModelFlags>
      <strHandlingFlags></strHandlingFlags>
      <strDamageFlags></strDamageFlags>
      <SubHandlingData>
        <Item type="CCarHandlingData">
          <strAdvancedFlags></strAdvancedFlags>
        </Item>
      </SubHandlingData>
    </Item>
  </HandlingData>
</CHandlingDataMgr>`;

const handlingDeclarations: IHandlingDeclaration[] = [
	{
		id: 'general',
		name: 'Основное',
		props: [
			{
				id: 'fMass',
				name: 'Масса',
				range: [0, 15000],
			},
			{
				id: 'vecCentreOfMassOffset',
				name: 'Центр массы',
				range: [-2, 2],
				type: 'vector3',
			},
			{
				id: 'fDriveBiasFront',
				name: 'Привод (1 - перед, 0 - зад, 0.5 - полный)',
				range: [0, 1],
			},
			{
				id: 'nInitialDriveGears',
				name: 'Количество передач',
				range: [1, 6],
			},
			{
				id: 'nMonetaryValue',
				name: 'Стоимость ТС',
				range: [0, 1000],
			},
		],
	},
	{
		id: 'enginebrakes',
		name: 'Ускорение и торможение',
		props: [
			{
				id: 'fInitialDragCoeff',
				name: 'Коэффициент аэродинамического сопротивления',
				range: [0, 120],
			},
			{
				id: 'vecInertiaMultiplier',
				name: 'Множитель вектора инерции',
				range: [-2, 2],
				type: 'vector3',
			},
			{
				id: 'fDriveInertia',
				name: 'Множитель инерции двигателя',
				range: [0, 2.5],
			},
			{
				id: 'fClutchChangeRateScaleUpShift',
				name: 'Множитель скорости сцепления при повышении передачи',
				range: [0, 10],
			},
			{
				id: 'fClutchChangeRateScaleDownShift',
				name: 'Множитель скорости сцепления при понижении передачи',
				range: [0, 10],
			},
			{
				id: 'fInitialDriveForce',
				name: 'Движущая сила на колесах',
				range: [0, 25],
			},
			{
				id: 'fBrakeForce',
				name: 'Сила торможения',
				range: [0, 1],
			},
			{
				id: 'fBrakeBiasFront',
				name: 'Распределение силы торможение между осями колес',
				range: [0, 1],
			},
			{
				id: 'fHandBrakeForce',
				name: 'Сила ручника',
				range: [0, 1],
			},
			{
				id: 'fTractionCurveMax',
				name: 'Сцепление колес в поворотах',
				range: [0, 3],
			},
			{
				id: 'fTractionCurveMin',
				name: 'Сцепление колес',
				range: [0, 3],
			},
			{
				id: 'fTractionCurveLateral',
				name: 'Дуговое сцепление колес',
				range: [10, 30],
			},
			{
				id: 'fTractionSpringDeltaMax',
				name: 'Расстояние от земли при котором теряется сцепление',
				range: [0, 0.5],
			},
			{
				id: 'fLowSpeedTractionLossMult',
				name: 'Сцепление с поверхностью на низких скоростях',
				range: [0, 2],
			},
			{
				id: 'fTractionLossMult',
				name: 'Сцепление на другой поверхности',
				range: [0, 1],
			},
			{
				id: 'fTractionBiasFront',
				name: 'Распределение тяги между осями колес',
				range: [0, 1],
			},
		],
	},
	{
		id: 'suspension',
		name: 'Подвеска',
		props: [
			{
				id: 'fSuspensionForce',
				name: 'Сила подвески',
				range: [0, 5],
			},
			{
				id: 'fSuspensionCompDamp',
				name: 'Интенсивность колебаний подвески',
				range: [0, 3],
			},
			{
				id: 'fSuspensionReboundDamp',
				name: 'Интенсивность колебаний подвески на высоких скоростях',
				range: [0, 3],
			},
			{
				id: 'fSuspensionUpperLimit',
				name: 'Максимальная высота подвески',
				range: [0, 1],
			},
			{
				id: 'fSuspensionLowerLimit',
				name: 'Минимальная высота подвески',
				range: [-1, 0],
			},
			{
				id: 'fSuspensionRaise',
				name: 'Высота подвески',
				range: [-1, 1],
			},
			{
				id: 'fSuspensionBiasFront',
				name: 'Смещение подвески вперед',
				range: [0, 1],
			},
			{
				id: 'fAntiRollBarForce',
				name: 'Сила стабилизатора подвески',
				range: [0, 2],
			},
			{
				id: 'fAntiRollBarBiasFront',
				name: 'Смещение стабилизатора подвески вперед',
				range: [0, 1],
			},
			{
				id: 'fRollCentreHeightFront',
				name: 'Высота центра крена (перед)',
				range: [0, 1],
			},
			{
				id: 'fRollCentreHeightRear',
				name: 'Высота центра крена (зад)',
				range: [0, 1],
			},
		],
	},
	{
		id: 'randomstuff',
		name: 'Разное',
		props: [
			// {
			// 	id: 'vecSeatOffsetDist',
			// 	name: 'Вектор расстояния от двери до сидения',
			// 	range: [-2, 2],
			// 	type: 'vector3',
			// },
			{
				id: 'fPercentSubmerged',
				name: 'Процент погружения в воду',
				range: [0, 100],
			},
			{
				id: 'fSteeringLock',
				name: 'Максимальный угол поворота колес (и руля)',
				range: [1, 90],
			},
			{
				id: 'fCollisionDamageMult',
				name: 'Множитель урона от столкновений',
				range: [0, 10],
			},
			{
				id: 'fWeaponDamageMult',
				name: 'Множитель урона от оружия',
				range: [0, 10],
			},
			{
				id: 'fDeformationDamageMult',
				name: 'Множитель урона от деформации (далее ломается)',
				range: [0, 10],
			},
			{
				id: 'fEngineDamageMult',
				name: 'Множитель урона двигателя (взрыв или выход из строя)',
				range: [0, 10],
			},
			{
				id: 'fPetrolTankVolume',
				name: 'Кол-во вытекаемого из бензобака топлива',
				range: [0, 250],
			},
			{
				id: 'fOilVolume',
				name: 'Кол-во черного дыма движка',
				range: [0, 10],
			},
		],
	},
	{
		id: 'strModelFlags',
		name: 'Флаги модели',
		props: [
			{
				id: 'MF_IS_VAN',
				type: 'flag',
			},
			{
				id: 'MF_IS_BUS',
				type: 'flag',
			},
			{
				id: 'MF_IS_LOW',
				type: 'flag',
			},
			{
				id: 'MF_IS_BIG',
				type: 'flag',
			},
			{
				id: 'MF_ABS_STD',
				type: 'flag',
			},
			{
				id: 'MF_ABS_OPTION',
				type: 'flag',
			},
			{
				id: 'MF_ABS_ALT_STD',
				type: 'flag',
			},
			{
				id: 'MF_ABS_ALT_OPTION',
				type: 'flag',
			},
			{
				id: 'MF_NO_DOORS',
				type: 'flag',
			},
			{
				id: 'MF_TANDEM_SEATING',
				type: 'flag',
			},
			{
				id: 'MF_SIT_IN_BOAT',
				type: 'flag',
			},
			{
				id: 'MF_HAS_TRACKS',
				type: 'flag',
			},
			{
				id: 'MF_NO_EXHAUST',
				type: 'flag',
			},
			{
				id: 'MF_DOUBLE_EXHAUST',
				type: 'flag',
			},
			{
				id: 'MF_NO_1STPERSON_LOOKBEHIND',
				type: 'flag',
			},
			{
				id: 'MF_CAN_ENTER_IF_NO_DOOR',
				type: 'flag',
			},
			{
				id: 'MF_AXLE_F_TORSION',
				type: 'flag',
			},
			{
				id: 'MF_AXLE_F_SOLID',
				type: 'flag',
			},
			{
				id: 'MF_AXLE_F_MCPHERSON',
				type: 'flag',
			},
			{
				id: 'MF_ATTACH_PED_TO_BODYSHELL',
				type: 'flag',
			},
			{
				id: 'MF_AXLE_R_TORSION',
				type: 'flag',
			},
			{
				id: 'MF_AXLE_R_MCPHERSON',
				type: 'flag',
			},
			{
				id: 'MF_DONT_FORCE_GRND_CLEARANCE',
				type: 'flag',
			},
			{
				id: 'MF_DONT_RENDER_STEER',
				type: 'flag',
			},
			{
				id: 'MF_NO_WHEEL_BURST',
				type: 'flag',
			},
			{
				id: 'MF_INDESTRUCTIBLE',
				type: 'flag',
			},
			{
				id: 'MF_DOUBLE_FRONT_WHEELS',
				type: 'flag',
			},
			{
				id: 'MF_IS_RC',
				type: 'flag',
			},
			{
				id: 'MF_DOUBLE_REAR_WHEELS',
				type: 'flag',
			},
			{
				id: 'MF_NO_WHEEL_BREAK',
				type: 'flag',
			},
			{
				id: 'MF_IS_HATCHBACK',
				type: 'flag',
			},
		],
	},
	{
		id: 'strHandlingFlags',
		name: 'Флаги хендлинга',
		props: [
			{
				id: 'HF_SMOOTHED_COMPRESSION',
				type: 'flag',
			},
			{
				id: 'HF_REDUCED_MOD_MASS',
				type: 'flag',
			},
			{
				id: 'HF_REDUCED_MOD_MASS',
				type: 'flag',
			},
			{
				id: 'HF_HAS_RALLY_TYRES',
				type: 'flag',
			},
			{
				id: 'HF_NO_HANDBRAKE',
				type: 'flag',
			},
			{
				id: 'HF_STEER_REARWHEELS',
				type: 'flag',
			},
			{
				id: 'HF_HANDBRAKE_REARWHEELSTEER',
				type: 'flag',
			},
			{
				id: 'HF_STEER_ALL_WHEELS',
				type: 'flag',
			},
			{
				id: 'HF_FREEWHEEL_NO_GAS',
				type: 'flag',
			},
			{
				id: 'HF_NO_REVERSE',
				type: 'flag',
			},
			{
				id: 'HF_REDUCED_RIGHTING_FORCE',
				type: 'flag',
			},
			{
				id: 'HF_STEER_NO_WHEELS',
				type: 'flag',
			},
			{
				id: 'HF_CVT',
				type: 'flag',
			},
			{
				id: 'HF_ALT_EXT_WHEEL_BOUNDS_BEH',
				type: 'flag',
			},
			{
				id: 'HF_DONT_RAISE_BOUNDS_AT_SPEED',
				type: 'flag',
			},
			{
				id: 'HF_EXT_WHEEL_BOUNDS_COL',
				type: 'flag',
			},
			{
				id: 'HF_LESS_SNOW_SINK',
				type: 'flag',
			},
			{
				id: 'HF_TYRES_CAN_CLIP',
				type: 'flag',
			},
			{
				id: 'HF_REDUCED_DRIVE_OVER_DAMAGE',
				type: 'flag',
			},
			{
				id: 'HF_ALT_EXT_WHEEL_BOUNDS_SHRINK',
				type: 'flag',
			},
			{
				id: 'HF_OFFROAD_ABILITIES',
				type: 'flag',
			},
			{
				id: 'HF_OFFROAD_ABILITIES_X2',
				type: 'flag',
			},
			{
				id: 'HF_TYRES_RAISE_SIDE_IMPACT_THRESHOLD',
				type: 'flag',
			},
			{
				id: 'HF_OFFROAD_INCREASED_GRAVITY_NO_FOLIAGE_DRAG',
				type: 'flag',
			},
			{
				id: 'HF_ENABLE_LEAN',
				type: 'flag',
			},
			{
				id: 'HF_FORCE_NO_TC_OR_SC',
				type: 'flag',
			},
			{
				id: 'HF_HEAVYARMOUR',
				type: 'flag',
			},
			{
				id: 'HF_ARMOURED',
				type: 'flag',
			},
			{
				id: 'HF_SELF_RIGHTING_IN_WATER',
				type: 'flag',
			},
			{
				id: 'HF_IMPROVED_RIGHTING_FORCE',
				type: 'flag',
			},
			{
				id: 'HF_LOW_SPEED_WHEELIES',
				type: 'flag',
			},
			{
				id: 'HF_LAST_AVAILABLE_FLAG',
				type: 'flag',
			},
		],
	},
	// {
	// 	id: 'strDamageFlags',
	// 	name: 'Флаги на повреждения',
	// 	props: [
	// 		{
	// 			id: 'DF_DRIVER_SIDE_FRONT_DOOR',
	// 			type: 'flag',
	// 		},
	// 		{
	// 			id: 'DF_DRIVER_SIDE_REAR_DOOR',
	// 			type: 'flag',
	// 		},
	// 		{
	// 			id: 'DF_DRIVER_PASSENGER_SIDE_FRONT_DOOR',
	// 			type: 'flag',
	// 		},
	// 		{
	// 			id: 'DF_DRIVER_PASSENGER_SIDE_REAR_DOOR',
	// 			type: 'flag',
	// 		},
	// 		{
	// 			id: 'DF_BONNET',
	// 			type: 'flag',
	// 		},
	// 		{
	// 			id: 'DF_BOOT',
	// 			type: 'flag',
	// 		},
	// 	],
	// },
	{
		id: 'strAdvancedFlags',
		name: 'Расширенные флаги',
		props: [
			{
				id: 'CF_DIFF_FRONT',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_REAR',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_CENTRE',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LIMITED_FRONT',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LIMITED_REAR',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LIMITED_CENTRE',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LOCKING_FRONT',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LOCKING_REAR',
				type: 'flag',
			},
			{
				id: 'CF_DIFF_LOCKING_CENTRE',
				type: 'flag',
			},
			{
				id: 'CF_GEARBOX_FULL_AUTO',
				type: 'flag',
			},
			{
				id: 'CF_GEARBOX_MANUAL',
				type: 'flag',
			},
			{
				id: 'CF_GEARBOX_DIRECT_SHIFT',
				type: 'flag',
			},
			{
				id: 'CF_GEARBOX_ELECTRIC',
				type: 'flag',
			},
			{
				id: 'CF_ASSIST_TRACTION_CONTROL',
				type: 'flag',
			},
			{
				id: 'CF_ASSIST_STABILITY_CONTROL',
				type: 'flag',
			},
			{
				id: 'CF_ALLOW_REDUCED_SUSPENSION_FORCE',
				type: 'flag',
			},
			{
				id: 'CF_HARD_REV_LIMIT',
				type: 'flag',
			},
			{
				id: 'CF_HOLD_GEAR_WITH_WHEELSPIN',
				type: 'flag',
			},
			{
				id: 'CF_INCREASE_SUSPENSION_FORCE_WITH_SPEED',
				type: 'flag',
			},
			{
				id: 'CF_BLOCK_INCREASED_ROT_VELOCITY_WITH_DRIVE_FORCE',
				type: 'flag',
			},
			{
				id: 'CF_REDUCED_SELF_RIGHTING_SPEED',
				type: 'flag',
			},
			{
				id: 'CF_CLOSE_RATIO_GEARBOX',
				type: 'flag',
			},
			{
				id: 'CF_FORCE_SMOOTH_RPM',
				type: 'flag',
			},
			{
				id: 'CF_ALLOW_TURN_ON_SPOT',
				type: 'flag',
			},
			{
				id: 'CF_CAN_WHEELIE',
				type: 'flag',
			},
			{
				id: 'CF_ENABLE_WHEEL_BLOCKER_SIDE_IMPACTS',
				type: 'flag',
			},
			{
				id: 'CF_FIX_OLD_BUGS',
				type: 'flag',
			},
			{
				id: 'CF_USE_DOWNFORCE_BIAS',
				type: 'flag',
			},
			{
				id: 'CF_REDUCE_BODY_ROLL_WITH_SUSPENSION_MODS',
				type: 'flag',
			},
			{
				id: 'CF_ALLOWS_EXTENDED_MODS',
				type: 'flag',
			},
		],
	},
];

const Hedit: React.FC = () => {
	const [tabIndex, setTabIndex] = useState(0);
	const handlingMode = useObservable(
		rage.vehicles.getLocalVehicleHandlingMode(),
	);
	const [handlingTabs, setHandlingTabs] = useState<IHandlingTab[]>([]);
	const [handlingContents, setHandlingContents] = useState<IHandlingContent[]>(
		[],
	);

	useEffect(() => {
		const fetchVehicleHandling = async () => {
			try {
				const [stockHandling, handling] = await Promise.all([
					rage.vehicles.getLocalVehicleStockHandling(),
					rage.vehicles.getLocalVehicleHandling(handlingMode),
				]);

				const properHandling: VehicleHandling = {};
				for (const [prop, stockValue] of Object.entries(stockHandling)) {
					properHandling[prop] =
						handling?.[prop] != null ? handling[prop] : stockValue;
				}

				return properHandling;
			} catch (error) {
				rage.notifications.showException(error);
				return null;
			}
		};

		fetchVehicleHandling()
			.then((handling) => {
				if (!handling) {
					return null;
				}

				const newHandlingTabs: IHandlingTab[] = [];
				const newHandlingContents: IHandlingContent[] = [];

				handlingDeclarations.forEach((tab, tabIndex) => {
					newHandlingTabs.push({
						id: tab.id,
						name: tab.name,
						index: tabIndex,
					});

					tab.props.forEach((prop, propIndex) => {
						const propType = prop.type || 'float';
						if (tab.id.endsWith('Flags')) {
							const flagValue = handling[tab.id];
							if (typeof flagValue === 'number') {
								const propValue = (flagValue & (1 << propIndex)) === 0 ? 0 : 1;
								newHandlingContents.push({
									id: prop.id,
									type: propType,
									value: propValue,
									index: tabIndex,
								});
							}
						} else {
							let propValue = handling[prop.id];
							if (propValue === undefined) {
								propValue = null;
							}

							if (typeof propValue === 'number') {
								propValue = +propValue.toFixed(3);
							} else if (typeof propValue === 'object' && propValue !== null) {
								propValue.x = +propValue.x.toFixed(3);
								propValue.y = +propValue.y.toFixed(3);
								propValue.z = +propValue.z.toFixed(3);
							}

							if (propValue != null) {
								newHandlingContents.push({
									id: prop.id,
									name: prop.name!,
									type: propType,
									range: prop.range,
									value: propValue,
									index: tabIndex,
								});
							}
						}
					});
				});

				setHandlingTabs(newHandlingTabs);
				setHandlingContents(newHandlingContents);
			})
			.catch((err) => rage.notifications.showException(err));
	}, [handlingMode]);

	const handleInputChangeFloat = useCallback(
		(index: number, newValue: string) => {
			const newContents = _.cloneDeep(handlingContents);
			newContents[index] = {
				...newContents[index],
				value: parseFloat(newValue),
			};

			setHandlingContents(newContents);
		},
		[handlingContents],
	);

	const handleInputChangeVector3 = useCallback(
		(index: number, coord: 'x' | 'y' | 'z', newValue: string) => {
			const newContents = _.cloneDeep(handlingContents);
			newContents[index].value = {
				...(newContents[index].value as IVector3),
				[coord]: parseFloat(newValue),
			};

			setHandlingContents(newContents);
		},
		[handlingContents],
	);

	const handleInputChangeFlag = useCallback(
		(index: number, bitSet: boolean) => {
			handlingContents[index].value = bitSet ? 1 : 0;
		},
		[handlingContents],
	);

	const parseHandlingValues: () => VehicleHandling = useCallback(() => {
		const handling: VehicleHandling = {};

		for (const tab of handlingTabs) {
			if (tab.id.endsWith('Flags')) {
				const bitArray = handlingContents
					.filter((entry) => entry.index === tab.index)
					.map((entry) => entry.value!.toString())
					.reverse();
				handling[tab.id] = parseInt(bitArray.join(''), 2);
			} else {
				handlingContents
					.filter((entry) => entry.index === tab.index)
					.forEach((entry) => {
						handling[entry.id] = entry.value;
					});
			}
		}

		return handling;
	}, [handlingTabs, handlingContents]);

	const copyToClipboard = useCallback(() => {
		const handling = parseHandlingValues();

		let handlingRecords = handlingTpl;
		Object.entries(handling).forEach(([name, value]) => {
			if (name.startsWith('f')) {
				handlingRecords = handlingRecords.replace(
					`<${name} value="" />`,
					`<${name} value="${(value as number).toFixed(6)}" />`,
				);
			} else if (name.startsWith('n')) {
				handlingRecords = handlingRecords.replace(
					`<${name} value="" />`,
					`<${name} value="${value}" />`,
				);
			} else if (name.startsWith('str')) {
				if (name.endsWith('Flags')) {
					handlingRecords = handlingRecords.replace(
						`<${name}></${name}>`,
						`<${name}>${value?.toString(16).toUpperCase() || 'NULL'}</${name}>`,
					);
				} else {
					handlingRecords = handlingRecords.replace(
						`<${name}></${name}>`,
						`<${name}>${value}</${name}>`,
					);
				}
				// } else if (name === 'vecSeatOffsetDist') {
				// 	for (const [coord, subValue] of Object.entries(
				// 		value as IHandlingVector,
				// 	)) {
				// 		const partialName = name.substring(3);
				// 		handlingRecords = handlingRecords.replace(
				// 			`<f${partialName}${coord.toUpperCase()} value="" />`,
				// 			`<f${partialName}${coord.toUpperCase()} value="${subValue.toFixed(
				// 				6,
				// 			)}" />`,
				// 		);
				// 	}
			} else if (name.startsWith('vec')) {
				const { x, y, z } = value as IVector3;
				handlingRecords = handlingRecords.replace(
					`<${name} x="" y="" z="" />`,
					`<${name} x="${x.toFixed(6)}" y="${y.toFixed(6)}" z="${z.toFixed(
						6,
					)}" />`,
				);
			}
		});

		rage.input.copyToClipboard(handlingRecords);
	}, [parseHandlingValues]);

	const applyHandling = useCallback(() => {
		const handling = parseHandlingValues();
		rage.vehicles
			.setLocalVehicleHandling(handling, handlingMode)
			.catch((err) => rage.notifications.showException(err));
	}, [handlingMode, parseHandlingValues]);

	return (
		<PanelContainer>
			<TabListContainer>
				{handlingTabs.map((tab, index) => (
					<TabContainer
						key={`tab#${index}`}
						className={tabIndex === index ? 'selected' : ''}
						onClick={() => setTabIndex(index)}
					>
						{tab.name}
					</TabContainer>
				))}
			</TabListContainer>
			<ContentContainerWrapper>
				<ContentContainer>
					{handlingContents.map((prop, index) => {
						const rowKey = `row#${index}#${tabIndex}`;
						if (prop.index !== tabIndex) {
							return null;
						}

						if (prop.type === 'float') {
							const propValue = prop.value as number;
							return (
								<ContentRowContainer key={rowKey}>
									<ContentRowText>{prop.name}</ContentRowText>
									<ContentInputRange range={prop.range!}>
										<ContentInputFloat
											defaultValue={propValue}
											onChange={(e) =>
												handleInputChangeFloat(index, e.target.value)
											}
										/>
									</ContentInputRange>
								</ContentRowContainer>
							);
						} else if (prop.type === 'vector3') {
							const propValue = prop.value as IVector3;

							return (
								<ContentRowContainer key={rowKey}>
									<ContentRowText>{prop.name}</ContentRowText>
									<ContentInputRange range={prop.range!}>
										<ContentInputFloat
											defaultValue={propValue.x}
											onChange={(e) =>
												handleInputChangeVector3(index, 'x', e.target.value)
											}
										/>
										<ContentInputFloat
											defaultValue={propValue.y}
											onChange={(e) =>
												handleInputChangeVector3(index, 'y', e.target.value)
											}
										/>
										<ContentInputFloat
											defaultValue={propValue.z}
											onChange={(e) =>
												handleInputChangeVector3(index, 'z', e.target.value)
											}
										/>
									</ContentInputRange>
								</ContentRowContainer>
							);
						} else if (prop.type === 'flag') {
							return (
								<ContentRowContainer
									key={rowKey}
									style={{ justifyContent: 'flex-start' }}
								>
									<ContentInputFlagContainer>
										<ContentInputFlag
											defaultChecked={prop.value === 1}
											onChange={(e) =>
												handleInputChangeFlag(index, e.target.checked)
											}
										/>
										<ContentRowText>{prop.id}</ContentRowText>
									</ContentInputFlagContainer>
								</ContentRowContainer>
							);
						}
					})}
				</ContentContainer>
				<ButtonContainer>
					<button onClick={copyToClipboard}>Скопировать</button>
					<button onClick={applyHandling}>Применить</button>
				</ButtonContainer>
			</ContentContainerWrapper>
		</PanelContainer>
	);
};

export { Hedit };
